import { Controller, Path } from 'react-hook-form';
import React, { useEffect } from 'react';
import { useFormContext } from '@alfa-retail-react/components/form-hook';

import { OrderFormValues } from 'app/logic/pages/order/OrderPage';
import { OrderFormStore } from 'app/logic/pages/order/OrderFormStore';
import {
    ConditionAgreementFormItem,
    MARKETING_COMMUNICATION_FIELD_NAME,
} from 'app/logic/ConditionAgreementFormItem';
import { MarketingCommunicationLink } from 'app/constants/links';
import { useI18nContext } from 'app/store/сontext/I18nContext';
import CollapseSection from '@alfa-retail-react/components/collapse-section';
import { Checkbox } from '@alfa-retail-react/components';
import { runInAction } from 'mobx';

import './MarketingConsentFormItem.less';
import {
    MarketingConsent,
    MarketingConsentType,
} from 'app/model/MarketingConsent';
import { observer } from 'mobx-react';

export interface MarketingConsentFormItemProps {
    checked: boolean;
    /**
     * Хранилище состояния страницы ввода личных данных.
     */
    orderFormStore: OrderFormStore;
}

export const IS_MARKETING_CONSENT_SIGNED_FORM_ITEM_NAME =
    'isMarketingConsentSigned';
export const MARKETING_CONSENT_FORM_ITEM_NAME = 'marketingConsent';

const CONSENT_TYPE = {
    [MarketingConsentType.MESSENGER]: 'messenger',
    [MarketingConsentType.PHONE_CALL]: 'phone_call',
    [MarketingConsentType.EMAIL]: 'email',
    [MarketingConsentType.PUSH]: 'push',
};

const consentTypesList = Object.getOwnPropertyNames(CONSENT_TYPE) as Array<
    keyof typeof CONSENT_TYPE
>;

const getMarketingConsentKey = (type: keyof typeof CONSENT_TYPE) =>
    `creditCard_marketing_consent_${CONSENT_TYPE[type]}`;

const HEADER_CLASS = 'communicationTypesListHeader';

const getIsOpen = () => {
    const headerArrow = document.querySelector(`.${HEADER_CLASS} > svg`);

    if (headerArrow) {
        const style = window.getComputedStyle(headerArrow);

        return style.transform !== 'none';
    }

    return false;
};

const handleHeaderClick = () => {
    const node = document.querySelector(`.${HEADER_CLASS}`);

    if (node) {
        node.dispatchEvent(
            new MouseEvent('click', {
                view: window,
                bubbles: true,
                cancelable: true,
                buttons: 1,
            }),
        );
    }
};

const getIsAllUnsigned = (consentList: MarketingConsent[]) =>
    consentList?.every((item) => !item.isSigned);

/**
 * Компонент установки согласий на каналы маркетинговой коммуникации
 * @constructor
 */
export const MarketingConsentFormItem = observer(
    ({ checked, orderFormStore }: MarketingConsentFormItemProps) => {
        const { t } = useI18nContext();
        const { control, handleMultipleErrors, setFieldElement } =
            useFormContext<OrderFormValues>();
        const isTypesListEnabled = (window as any).env.FEATURE_FLAGS
            ?.marketingConsentTypes;

        const handleConsentTypeSigned = () => {
            const isAllUnsigned = getIsAllUnsigned(
                orderFormStore.marketingConsentList,
            );

            if (isAllUnsigned) {
                orderFormStore.setIsMarketingConsentSigned(false);
                handleHeaderClick();
            } else if (!orderFormStore.isMarketingConsentSigned) {
                orderFormStore.setIsMarketingConsentSigned(true);
            }
        };

        useEffect(() => {
            if (!isTypesListEnabled) {
                return;
            }

            const isTypesOpen = getIsOpen();

            if (orderFormStore.isMarketingConsentSigned && !isTypesOpen) {
                handleHeaderClick();
            }

            if (
                getIsAllUnsigned(orderFormStore.marketingConsentList) &&
                orderFormStore.isMarketingConsentSigned
            ) {
                orderFormStore.setAllMarketingConsentTypeSigned(true);
            }

            if (!orderFormStore.isMarketingConsentSigned) {
                orderFormStore.setAllMarketingConsentTypeSigned(false);
            }
        }, [orderFormStore.isMarketingConsentSigned]);

        return (
            <div>
                <ConditionAgreementFormItem<OrderFormValues, unknown>
                    label={t([
                        'creditCard_marketing_consent_title',
                        <MarketingCommunicationLink />,
                    ])}
                    isRequired={false}
                    name={MARKETING_COMMUNICATION_FIELD_NAME}
                    className={MARKETING_COMMUNICATION_FIELD_NAME}
                    defaultValue={checked}
                    checked={checked}
                    onChange={(checked) =>
                        orderFormStore.setIsMarketingConsentSigned(checked)
                    }
                />
                {isTypesListEnabled && (
                    <CollapseSection
                        open={false}
                        className="communicationTypesList"
                        classNameHeaderWrapper={HEADER_CLASS}
                        header={t(
                            'creditCard_marketing_consent_types_list_header',
                        )}
                    >
                        {consentTypesList.map((type, index) => {
                            const fieldName: Path<OrderFormValues> = `${MARKETING_CONSENT_FORM_ITEM_NAME}.${index}.isSigned`;
                            const fieldValue =
                                orderFormStore.getMarketingConsentTypeSigned(
                                    type,
                                );

                            return (
                                <Controller
                                    key={type}
                                    name={fieldName}
                                    control={control}
                                    defaultValue={orderFormStore.getMarketingConsentTypeSigned(
                                        type,
                                    )}
                                    render={({ field, fieldState }) => (
                                        <Checkbox
                                            ref={(element) => {
                                                field.ref(element);
                                                setFieldElement(
                                                    fieldName,
                                                    element,
                                                );
                                            }}
                                            size="m"
                                            error={handleMultipleErrors(
                                                fieldState.error,
                                            )}
                                            checked={fieldValue}
                                            label={t(
                                                getMarketingConsentKey(type),
                                            )}
                                            onChange={(event, payload) => {
                                                field.onChange(event);
                                                runInAction(() => {
                                                    orderFormStore.setMarketingConsentTypeSigned(
                                                        type,
                                                        payload.checked,
                                                    );
                                                });

                                                handleConsentTypeSigned();
                                            }}
                                        />
                                    )}
                                />
                            );
                        })}
                    </CollapseSection>
                )}
            </div>
        );
    },
);
